/** @jsx isolateComponent */
import { Fragment, useState, useEffect } from 'react';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Alert, Link } from '@zapier/design-system';
import { writeCookie, readCookie } from '@zapier/cookies';

import type { ResponseData } from '../useAlertBanner/useAlertBannerData';

import { BannerProps } from './types';

export const shouldRender = (
  data: ResponseData,
  _shouldRender: boolean = false
) => {
  const { currentAccount } = data;
  const hasDismissedBanner = readCookie('dismissedOverdueInvoiceBanner');

  return currentAccount?.hasOverdueInvoice && !(hasDismissedBanner === 'true');
};

export const Component = function OverdueInvoice(props: BannerProps) {
  const [isExcludedUrl, setIsExcludedUrl] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const { data } = props;
  const { currentAccount } = data;
  const periodEndDate = data.currentAccount.periodEndDate;

  const hasBillingPrivs =
    currentAccount.currentUserRole &&
    ['owner', 'super_admin'].includes(currentAccount.currentUserRole);

  useEffect(() => {
    setIsExcludedUrl(window.location.pathname.includes('checkout/invoice/pay'));
  }, []);

  const expiryDate = () => {
    if (periodEndDate) {
      return periodEndDate;
    }
    const defaultExpiry = new Date();
    defaultExpiry.setDate(defaultExpiry.getDate() + 30);
    return defaultExpiry;
  };

  const onDismiss = () => {
    writeCookie('dismissedOverdueInvoiceBanner', 'true', {
      category: 'essential',
      expires: expiryDate(),
    });
    setIsDismissed(true);
  };

  if (isDismissed || isExcludedUrl) {
    return null;
  }

  const title = (
    <span>
      There was a problem with your last payment.{' '}
      {hasBillingPrivs ? (
        <Link href="/app/settings/billing">Fix it now</Link>
      ) : (
        <Fragment>
          Please have an account owner update the payment information.
        </Fragment>
      )}
    </span>
  );

  return (
    <Alert
      onDismiss={onDismiss}
      layout={props.layout}
      title={title}
      type="error"
    />
  );
};
